import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface MonthPickerDropdownProps {
  selectedDate?: Date;
  onDateSelect: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  compact?: boolean;
  selectedColor?: string;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export default function MonthPickerDropdown({
  selectedDate,
  onDateSelect,
  minDate,
  maxDate,
  compact = false,
  selectedColor = 'var(--anl-warning)',
}: MonthPickerDropdownProps) {
  const currentDate = new Date();
  const minAllowedYear = minDate ? minDate.getFullYear() : currentDate.getFullYear() - 10;
  const [viewYear, setViewYear] = useState(
    Math.max(selectedDate?.getFullYear() || currentDate.getFullYear(), minAllowedYear),
  );

  const isMonthDisabled = (monthIndex: number): boolean => {
    const testDate = new Date(viewYear, monthIndex, 1);

    if (minDate) {
      const minMonthStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      if (testDate < minMonthStart) return true;
    }

    if (maxDate) {
      const maxMonthStart = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      if (testDate > maxMonthStart) return true;
    }

    const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    if (testDate > currentMonthStart) return true;

    return false;
  };

  const isMonthSelected = (monthIndex: number): boolean => {
    if (!selectedDate) return false;
    return selectedDate.getFullYear() === viewYear && selectedDate.getMonth() === monthIndex;
  };

  const handleMonthClick = (monthIndex: number) => {
    if (isMonthDisabled(monthIndex)) return;
    const newDate = new Date(viewYear, monthIndex, 1);
    onDateSelect(newDate);
  };

  const canGoPrevYear = viewYear > minAllowedYear;
  const canGoNextYear = viewYear < currentDate.getFullYear();

  if (compact) {
    return (
      <div
        className="w-full rounded-md border border-[var(--anl-border)] p-2 shadow-lg"
        style={{ backgroundColor: '#454b4d' }}
      >
        <div className="mb-2 flex items-center justify-between">
          <button
            onClick={() => setViewYear(prev => prev - 1)}
            disabled={!canGoPrevYear}
            className={`rounded p-1 ${
              canGoPrevYear
                ? 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
                : 'cursor-not-allowed text-slate-500'
            }`}
          >
            <ChevronLeft size={14} />
          </button>

          <span className="text-sm font-medium text-slate-100">{viewYear}</span>

          <button
            onClick={() => setViewYear(prev => prev + 1)}
            disabled={!canGoNextYear}
            className={`rounded p-1 ${
              canGoNextYear
                ? 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
                : 'cursor-not-allowed text-slate-500'
            }`}
          >
            <ChevronRight size={14} />
          </button>
        </div>

        <div className="grid grid-cols-4 gap-1">
          {MONTHS.map((month, index) => {
            const isDisabled = isMonthDisabled(index);
            const isSelected = isMonthSelected(index);

            return (
              <button
                key={month}
                onClick={() => handleMonthClick(index)}
                disabled={isDisabled}
                className={`rounded px-1 py-1 text-xs transition-colors ${
                  isSelected
                    ? 'text-white'
                    : isDisabled
                      ? 'cursor-not-allowed text-slate-500'
                      : 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
                } `}
                style={isSelected ? { backgroundColor: selectedColor } : undefined}
              >
                {month.slice(0, 3)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-64 rounded-lg border border-[var(--anl-border)] p-4 shadow-lg"
      style={{ backgroundColor: '#454b4d' }}
    >
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() => setViewYear(prev => prev - 1)}
          disabled={!canGoPrevYear}
          className={`rounded p-1 ${
            canGoPrevYear
              ? 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
              : 'cursor-not-allowed text-slate-500'
          }`}
        >
          <ChevronLeft size={16} />
        </button>

        <span className="font-medium text-slate-100">{viewYear}</span>

        <button
          onClick={() => setViewYear(prev => prev + 1)}
          disabled={!canGoNextYear}
          className={`rounded p-1 ${
            canGoNextYear
              ? 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
              : 'cursor-not-allowed text-slate-500'
          }`}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {MONTHS.map((month, index) => {
          const isDisabled = isMonthDisabled(index);
          const isSelected = isMonthSelected(index);

          return (
            <button
              key={month}
              onClick={() => handleMonthClick(index)}
              disabled={isDisabled}
              className={`rounded px-2 py-2 text-sm transition-colors ${
                isSelected
                  ? 'text-white'
                  : isDisabled
                    ? 'cursor-not-allowed text-slate-500'
                    : 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
              } `}
              style={isSelected ? { backgroundColor: selectedColor } : undefined}
            >
              {month.slice(0, 3)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
